import { useEffect } from "react";
import { Link, useRoute } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, BookOpen, Loader2 } from "lucide-react";

interface HelpKbArticle {
  id: string;
  slug: string;
  title: string;
  body: string;
  tags: string[];
  updatedAt: string;
}

interface HelpKbArticleSummary {
  id: string;
  slug: string;
  title: string;
  tags: string[];
}

function renderBody(body: string): JSX.Element {
  const paragraphs = body.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return (
    <div className="space-y-4 text-sm leading-relaxed text-foreground">
      {paragraphs.map((p, i) => (
        <p key={i} className="whitespace-pre-wrap break-words" data-testid={`text-kb-paragraph-${i}`}>
          {p}
        </p>
      ))}
    </div>
  );
}

export default function HelpKbArticlePage() {
  const [, params] = useRoute<{ slug: string }>("/help-kb/:slug");
  const slug = params?.slug;

  const articleQuery = useQuery<HelpKbArticle>({
    queryKey: ["/api/help-kb/articles", slug],
    enabled: Boolean(slug),
  });

  const listQuery = useQuery<HelpKbArticleSummary[]>({
    queryKey: ["/api/help-kb/articles"],
    enabled: !slug,
  });

  useEffect(() => {
    if (articleQuery.data?.title) {
      document.title = `${articleQuery.data.title} — Help`;
    } else if (!slug) {
      document.title = "Help guides — Clarik";
    }
  }, [articleQuery.data?.title, slug]);

  if (!slug) {
    return (
      <div className="container mx-auto max-w-3xl py-8 px-4">
        <div className="flex items-center gap-2 mb-6">
          <BookOpen className="w-5 h-5 text-primary" />
          <h1 className="text-2xl font-semibold" data-testid="text-kb-index-title">Help guides</h1>
        </div>
        {listQuery.isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="grid gap-3">
            {(listQuery.data || []).map((a) => (
              <Link
                key={a.id}
                href={`/help-kb/${a.slug}`}
                className="block rounded-lg border p-4 hover:bg-muted/50 transition-colors"
                data-testid={`link-kb-article-${a.slug}`}
              >
                <div className="font-medium">{a.title}</div>
                {a.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {a.tags.slice(0, 4).map((t) => (
                      <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>
                    ))}
                  </div>
                )}
              </Link>
            ))}
            {(listQuery.data || []).length === 0 && (
              <p className="text-sm text-muted-foreground">No guides available yet.</p>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-3xl py-8 px-4">
      <Link href="/help-kb" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="link-kb-back">
        <ArrowLeft className="w-4 h-4" /> All guides
      </Link>
      {articleQuery.isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : articleQuery.isError || !articleQuery.data ? (
        <Card data-testid="card-kb-not-found">
          <CardHeader>
            <CardTitle>Guide not found</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              We couldn't find that guide. It may have been moved or renamed.
            </p>
            <div className="flex gap-2">
              <Link href="/help-kb"><Button variant="outline" size="sm">Browse all guides</Button></Link>
              <Link href="/feedback"><Button variant="ghost" size="sm">Tell us what you needed</Button></Link>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle data-testid="text-kb-article-title">{articleQuery.data.title}</CardTitle>
            {articleQuery.data.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-2">
                {articleQuery.data.tags.map((t) => (
                  <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>
                ))}
              </div>
            )}
          </CardHeader>
          <CardContent>{renderBody(articleQuery.data.body)}</CardContent>
        </Card>
      )}
    </div>
  );
}
